import React, { useState, useEffect } from 'react';
import { Check, Clock, Link as LinkIcon, Copy, ExternalLink } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Operations checklist items
const OPERATIONS_ITEMS = [
  { key: 'client_contract_copy_in_drive', label: 'Client contract copy in drive' },
  { key: 'client_is_invoiced', label: 'Client is invoiced' },
  { key: 'invoice_or_first_payment_made', label: 'Invoice or 1st payment made' },
  { key: 'kyc_completed_on_card', label: 'KYC completed on card' },
  { key: 'meeting_set_with_entire_team', label: 'Meeting set with entire team' },
  { key: 'user_access_granted', label: 'User access granted' },
];

const OperationsChecklist = ({ card, onUpdate }) => {
  const { isDark } = useTheme();
  const [checklist, setChecklist] = useState({});
  const [saving, setSaving] = useState(null);
  const [loginUrl, setLoginUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);

  // Initialize checklist from card data
  useEffect(() => {
    if (card?.operations_checklist) {
      setChecklist(card.operations_checklist);
    } else {
      // Initialize with empty checklist
      const initial = {};
      OPERATIONS_ITEMS.forEach(item => {
        initial[item.key] = { checked: false, checked_at: null };
      });
      setChecklist(initial);
    }
    // Initialize login URL
    setLoginUrl(card?.company_login_url || '');
  }, [card?.operations_checklist, card?.company_login_url]);

  const handleCheckboxChange = async (key) => {
    const isChecked = !checklist[key]?.checked;
    const now = new Date().toISOString();
    
    const newChecklist = {
      ...checklist,
      [key]: {
        checked: isChecked,
        checked_at: isChecked ? now : null
      }
    };
    
    setChecklist(newChecklist);
    setSaving(key);

    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          operations_checklist: newChecklist
        })
      });

      if (response.ok) {
        const updatedCard = await response.json();
        if (onUpdate) {
          onUpdate(updatedCard);
        }
      }
    } catch (error) {
      console.error('Failed to save operations checklist:', error);
      // Revert on error
      setChecklist(checklist);
    } finally {
      setSaving(null);
    }
  };

  const handleSaveLoginUrl = async () => {
    if (!loginUrl.trim()) return;
    setSavingUrl(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${card.card_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_login_url: loginUrl.trim()
        })
      });

      if (response.ok) {
        const updatedCard = await response.json();
        if (onUpdate) {
          onUpdate(updatedCard);
        }
      }
    } catch (error) {
      console.error('Failed to save login URL:', error);
    } finally {
      setSavingUrl(false);
    }
  };

  const copyLoginUrl = () => {
    navigator.clipboard.writeText(loginUrl);
    alert('Login URL copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate completion percentage
  const completedCount = OPERATIONS_ITEMS.filter(item => checklist[item.key]?.checked).length;
  const totalCount = OPERATIONS_ITEMS.length;
  const completionPercent = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="space-y-4">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Operations Checklist</h3>
        <div className="flex items-center gap-3">
          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {completedCount} of {totalCount} complete
          </div>
          <div className={`w-24 h-2 ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded-full overflow-hidden`}>
            <div 
              className={`h-full ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} transition-all duration-300`}
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <span className={`text-sm font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>{completionPercent}%</span>
        </div>
      </div>

      {/* Checklist items */}
      <div className={`${isDark ? 'bg-[#111] border-white/10 divide-white/5' : 'bg-white border-gray-200 divide-gray-100'} border rounded-sm divide-y`}>
        {OPERATIONS_ITEMS.map((item) => {
          const itemData = checklist[item.key] || { checked: false, checked_at: null };
          const isChecked = itemData.checked;
          const isSaving = saving === item.key;

          return (
            <div 
              key={item.key}
              className={`p-4 flex items-center justify-between ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors ${isSaving ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3">
                {/* Checkbox */}
                <button
                  onClick={() => handleCheckboxChange(item.key)}
                  disabled={isSaving}
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                    isChecked 
                      ? isDark ? 'bg-[#A2FF00] border-[#A2FF00]' : 'bg-green-500 border-green-500'
                      : isDark ? 'border-gray-600 hover:border-[#A2FF00]' : 'border-gray-300 hover:border-green-500'
                  }`}
                >
                  {isChecked && <Check className={`w-4 h-4 ${isDark ? 'text-black' : 'text-white'}`} strokeWidth={3} />}
                </button>

                {/* Label */}
                <span className={`font-medium ${isChecked ? 'text-gray-400 line-through' : isDark ? 'text-white' : 'text-gray-900'}`}>
                  {item.label}
                </span>
              </div>

              {/* Checked date */}
              {isChecked && itemData.checked_at && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatDate(itemData.checked_at)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* QSECDEF Login URL Field */}
      <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 mt-4`}>
        <div className="flex items-center gap-2 mb-3">
          <LinkIcon className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'}`} />
          <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>QSECDEF Login URL</h4>
        </div>
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-3`}>
          Enter the company&apos;s unique login URL for the QSECDEF platform.
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={loginUrl}
            onChange={(e) => setLoginUrl(e.target.value)}
            placeholder="https://portal.qsecdef.com/..."
            className={`flex-1 ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white placeholder-gray-600' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm px-3 py-2 text-sm focus:border-[#A2FF00] outline-none`}
          />
          {loginUrl && loginUrl === card?.company_login_url ? (
            <div className="flex gap-1">
              <button
                onClick={copyLoginUrl}
                className={`px-3 py-2 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-sm transition-colors`}
                title="Copy URL"
              >
                <Copy className="w-4 h-4" />
              </button>
              <a
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-3 py-2 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-sm transition-colors flex items-center`}
                title="Open URL"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <button
              onClick={handleSaveLoginUrl}
              disabled={savingUrl || !loginUrl.trim()}
              className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8FE600]' : 'bg-green-500 hover:bg-green-600'} text-black font-medium rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {savingUrl ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationsChecklist;
